import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  User, Mail, Phone, MapPin, Calendar, Shield, Ban, CheckCircle,
  Clock, CreditCard, ArrowLeft, Loader, TrendingUp
} from 'lucide-react';
import adminService, { User as UserType, UserTransaction } from '../../services/admin/adminService';
import toast from 'react-hot-toast';
import { getImageUrl } from '../../utils/imageHelper';
import { formatCurrency, getCurrencyByCountry } from '../../utils/formatters';

const UserDetails: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const [user, setUser] = useState<UserType | null>(null);
  const [transactions, setTransactions] = useState<UserTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (userId) {
      fetchUserDetails();
      fetchUserTransactions();
    }
  }, [userId]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      const data = await adminService.getUserById(parseInt(userId!));
      setUser(data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch user details');
      navigate('/admin/users');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserTransactions = async () => {
    try {
      setTransactionsLoading(true);
      const data = await adminService.getUserTransactions(parseInt(userId!), 1, 10);
      setTransactions(data.transactions);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch transactions');
    } finally {
      setTransactionsLoading(false);
    }
  };

  const handleUnsuspend = async () => {
    if (!user) return;
    try {
      setProcessing(true);
      await adminService.unsuspendUser(user.user_id);
      toast.success('User unsuspended successfully!');
      fetchUserDetails();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to unsuspend user');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };



  const getStatusBadge = (user: UserType) => {
    if (user.is_suspended) {
      return 'bg-danger-50 text-danger-600 border-danger-200';
    }
    if (user.is_active) {
      return 'bg-success-50 text-success-600 border-success-200';
    }
    return 'bg-secondary-50 text-secondary-600 border-secondary-200';
  };

  const getStatusText = (user: UserType) => {
    if (user.is_suspended) return 'Suspended';
    if (user.is_active) return 'Active';
    return 'Inactive';
  };

  const getTransactionStatusBadge = (status: string) => {
    const styles: any = {
      success: 'bg-success-50 text-success-600',
      pending: 'bg-warning-50 text-warning-600',
      failed: 'bg-danger-50 text-danger-600',
      refunded: 'bg-info-50 text-info-600',
      cancelled: 'bg-secondary-50 text-secondary-600'
    };
    return styles[status] || 'bg-secondary-50 text-secondary-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader className="animate-spin text-primary-500" size={48} />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-20">
        <p className="text-secondary-600">User not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/admin/users')}
            className="bg-secondary-100 hover:bg-secondary-200 text-secondary-700 p-2 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-secondary-900 mb-2">User Details</h1>
            <p className="text-secondary-600">View and manage user information</p>
          </div>
        </div>
        <div className="flex space-x-3">

        </div>
      </div>

      {/* User Info Card */}
      <div className="bg-white rounded-xl shadow-soft p-8">
        <div className="flex items-start space-x-6">
          {user.profile_image ? (
            <img
              src={getImageUrl(user.profile_image)}
              alt={user.full_name}
              className="w-32 h-32 rounded-full object-cover border-4 border-primary-100"
            />
          ) : (
            <div className="w-32 h-32 bg-primary-100 rounded-full flex items-center justify-center border-4 border-primary-200">
              <User className="text-primary-600" size={64} />
            </div>
          )}
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-3xl font-bold text-secondary-900">{user.full_name}</h2>
                <p className="text-secondary-600 mt-1 text-lg">{user.email}</p>
                <p className="text-sm text-secondary-500 mt-1">User ID: {user.user_id}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4 mt-4">
              <span className={`inline-flex items-center space-x-1 px-4 py-2 rounded-full text-sm font-semibold border-2 ${getStatusBadge(user)}`}>
                {user.is_suspended ? <Ban size={16} /> : user.is_active ? <CheckCircle size={16} /> : <Clock size={16} />}
                <span>{getStatusText(user)}</span>
              </span>
              {user.is_verified === 1 && (
                <span className="inline-flex items-center space-x-1 px-4 py-2 bg-success-50 text-success-600 rounded-full border-2 border-success-200">
                  <Shield size={16} />
                  <span className="text-sm font-semibold">Verified</span>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-soft border-l-4 border-primary-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary-600 mb-2">Total Spent</p>
              <p className="text-2xl font-bold text-primary-600">
                {(() => {
                  const curInfo = getCurrencyByCountry(user.country);
                  return formatCurrency(user.total_spent || 0, curInfo.code, curInfo.locale, curInfo.symbol);
                })()}
              </p>
            </div>
            <TrendingUp className="text-primary-500" size={40} />
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-soft border-l-4 border-success-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary-600 mb-2">Total Transactions</p>
              <p className="text-2xl font-bold text-success-600">
                {user.total_transactions || 0}
              </p>
            </div>
            <CreditCard className="text-success-500" size={40} />
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-soft border-l-4 border-info-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary-600 mb-2">Member Since</p>
              <p className="text-lg font-bold text-info-600">
                {new Date(user.created_at).toLocaleDateString('en-IN', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </p>
            </div>
            <Calendar className="text-info-500" size={40} />
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="bg-white rounded-xl shadow-soft p-8">
        <h3 className="text-xl font-bold text-secondary-900 mb-6">Contact Information</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="flex items-start space-x-3">
            <div className="bg-primary-100 p-3 rounded-lg">
              <Mail size={24} className="text-primary-600" />
            </div>
            <div>
              <p className="text-sm text-secondary-500 mb-1">Email Address</p>
              <p className="text-secondary-900 font-semibold">{user.email}</p>
            </div>
          </div>
          {user.mobile_number && (
            <div className="flex items-start space-x-3">
              <div className="bg-success-100 p-3 rounded-lg">
                <Phone size={24} className="text-success-600" />
              </div>
              <div>
                <p className="text-sm text-secondary-500 mb-1">Mobile Number</p>
                <p className="text-secondary-900 font-semibold">{user.mobile_number}</p>
              </div>
            </div>
          )}
          {user.address && (
            <div className="flex items-start space-x-3">
              <div className="bg-info-100 p-3 rounded-lg">
                <MapPin size={24} className="text-info-600" />
              </div>
              <div>
                <p className="text-sm text-secondary-500 mb-1">Address</p>
                <p className="text-secondary-900 font-semibold">{user.address}</p>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Recent Transactions */}
      <div className="bg-white rounded-xl shadow-soft p-8">
        <h3 className="text-xl font-bold text-secondary-900 mb-6">Recent Transactions</h3>
        {transactionsLoading ? (
          <div className="text-center py-8">
            <Loader className="animate-spin text-primary-500 mx-auto" size={32} />
          </div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-12 bg-secondary-50 rounded-lg">
            <CreditCard size={64} className="text-secondary-300 mx-auto mb-4" />
            <p className="text-secondary-600 font-semibold">No transactions yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {transactions.map((tx) => (
              <div key={tx.transaction_id} className="border-2 border-secondary-200 rounded-lg p-4 hover:border-primary-300 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="bg-primary-100 p-3 rounded-lg">
                      <CreditCard size={24} className="text-primary-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-secondary-900 text-lg">{tx.business_name}</p>
                      <p className="text-sm text-secondary-500">{formatDate(tx.created_at)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-secondary-900 text-xl">
                      {(() => {
                        const curInfo = getCurrencyByCountry(user.country);
                        return formatCurrency(tx.amount, tx.currency_code || curInfo.code, 'en-IN', tx.currency_symbol || curInfo.symbol);
                      })()}
                    </p>
                    <span className={`inline-block mt-1 px-3 py-1 rounded-full text-xs font-semibold ${getTransactionStatusBadge(tx.status)}`}>
                      {tx.status}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      {user.is_suspended && (
        <div className="bg-white rounded-xl shadow-soft p-8">
          <h3 className="text-xl font-bold text-secondary-900 mb-4">User Actions</h3>
          <button
            onClick={handleUnsuspend}
            disabled={processing}
            className="bg-success-500 hover:bg-success-600 text-white px-6 py-3 rounded-lg font-semibold flex items-center space-x-2 transition-colors disabled:opacity-50"
          >
            {processing ? (
              <>
                <Loader className="animate-spin" size={20} />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <CheckCircle size={20} />
                <span>Unsuspend User</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default UserDetails;

